import { Subscription } from 'rxjs';
import {IWIZLayout} from '../../interfaces/iwiz-layout';
import { ChangeDetectorRef, ElementRef, HostBinding,
  Input, Renderer2, Directive, QueryList, ContentChildren, AfterContentInit, InjectionToken, OnDestroy } from '@angular/core';
import { IWIZ_COMPONENT, IWIZComponent} from '../../interfaces/iwiz-component';  
import { WizMathUtils } from '@wizeoo/akm-core-lib';

export const BASE_LAYOUT = new InjectionToken('BASE_LAYOUT');

/**
 * Classe abstraite de base pour tous les layouts wiz
 */
@Directive()
export abstract class WizBaseLayout implements IWIZLayout, AfterContentInit, OnDestroy {

  @ContentChildren(WizBaseLayout, {descendants: true}) baseLayoutChildren: QueryList<WizBaseLayout>;

  @ContentChildren(IWIZ_COMPONENT) iwizComponentChildren: QueryList<IWIZComponent>;

  @HostBinding('attr.wiz-disabled')
  public get attrIsDisabled(): string {
    return `${this._wizDisabled}`;
  }

  /**
   * @param elementRef ??
   * @param renderer ??
   * ??
   * The Renderer2 class is an abstraction provided by Angular in the form of a service
   * that allows to manipulate elements of your app without having to touch the DOM directly.
   */
  constructor(public elementRef: ElementRef, public  renderer: Renderer2, protected cdref: ChangeDetectorRef) {
    this._entityUID = WizMathUtils.createUUID();
  }

  private _subs: Subscription[] = [];

  private _numComponents: number = 0;
  private _numLayouts: number = 0;

  ngAfterContentInit() {
    this._numComponents = this.iwizComponentChildren.length;
    this._numLayouts = this.baseLayoutChildren.length;

    if (this._wizDisabledChanged) {
      this.manageWizDisabled(this.iwizComponentChildren);
      this.manageWizDisabled(this.baseLayoutChildren);
    }

    this._subs.push(this.iwizComponentChildren.changes.subscribe(c => {
      if (this._numComponents !== c.length && this._wizDisabledChanged) {
        this._numComponents = c.length;
        this.manageWizDisabled(this.iwizComponentChildren);
      }
    }));
    this._subs.push(this.baseLayoutChildren.changes.subscribe(c => {
      if (this._numLayouts !== c.length && this._wizDisabledChanged) {
        this._numLayouts = c.length
        this.manageWizDisabled(this.baseLayoutChildren);
      }
    }));
  }

  private manageWizDisabled(elements: QueryList<IWIZComponent> | QueryList<WizBaseLayout>) {
    if (!elements) {
      return;
    }

    elements.toArray().forEach(elem => {
      if (elem.wizDisabled !== this.wizDisabled) {
        if(!elem.undisableable) {
          elem.wizDisabled = this.wizDisabled;
        }
      }
    });
  }

  ngOnDestroy() {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  /* ###############################################
                    IDENTIFICATION
     ############################################### */

  /** UID du layout */
  private _entityUID: string;
  public get entityUID(): string {
    return this._entityUID;
  }

  /* ###############################################
                    CONTENU
     ############################################### */

  /** Nombre d'éléments enfants */
  protected numChildren: number = 0;

  /**
   * Accesseur à la collection d'éléments enfants
   * @returns any[]
   */
  public get children(): any[] {
    return this.elementRef.nativeElement.children;
  }

  /* #################################################
                    POSITIONNEMENTS
     ################################################# */

  /** gestion de la visibilité */
  @HostBinding('class.invisible-class') get visibility() {
    return this._visible === false;
  }
  
  private _visible: boolean = true;
  public get visible(): boolean {
    return this._visible;
  }
  @Input() public set visible(value: boolean) {
    this._visible = value;
    this.cdref.markForCheck();
  }

  /**
   * Style overflow hidden par défaut pour empêcher le débordement des éléments enfants
   */
  @HostBinding('style.overflow') overflow = 'hidden';

  /**
   * Modification de la propriété css overflow
   * @param overflow Valeur pour la propriété
   */
  @Input() public set wizOverflow(overflow: string) {
    this.overflow = overflow;
    this.cdref.markForCheck();
  }

  /** Gap en pixels */
  private _gap: number = 5;
  public get gap(): number {
    return this._gap;
  }

  @Input() public set gap(gap: number) {
    if (gap) {
      this._gap = gap;
      this.applyGap();
    }
    this.cdref.markForCheck();
  }

  /** Marge */
  private _margin: string;
  public get margin(): string {
    return this._margin;
  }

  @Input() public set margin(margin: string) {
    this._margin = margin;
    this.cdref.markForCheck();
  }

  /** Alignement vertical des éléments enfants */
  protected _verticalAlign: 'start' | 'center' | 'end' | 'flex-start' | 'flex-end' | 'space-between';
  public set verticalAlign(value: 'start' | 'center' | 'end' | 'flex-start' | 'flex-end' | 'space-between') {
    this._verticalAlign = value;
  }
  public get verticalAlign(): 'start' | 'center' | 'end' | 'flex-start' | 'flex-end' | 'space-between' {
    return this._verticalAlign;
  }

  /** Alignement horizontal des éléments enfants */
  protected _horizontalAlign: 'start' | 'center' | 'end' | 'space-between';
  public set horizontalAlign(value: 'start' | 'center' | 'end' | 'space-between') {
    this._horizontalAlign = value;
  }
  public get horizontalAlign(): 'start' | 'center' | 'end' | 'space-between' {
    return this._horizontalAlign;
  }

  /**
   * Application du gap. Méthode à overrider au besoin
   */
  protected applyGap(): void {
    // A overrider
  }

  protected _wizDisabled: boolean = false;
  protected _wizDisabledChanged: boolean = false;
  @Input()
  public set wizDisabled(v: boolean) {
    this.setWizDisabled(v);
  }
  public get wizDisabled(): boolean {
    return this.getWizDisabled();
  }

  // accesseurs mode Java pour override dans les classes héritées
  public setWizDisabled(v: boolean) {
    this._wizDisabled = v;
    this._wizDisabledChanged = true;
    this.manageWizDisabled(this.iwizComponentChildren);
    this.manageWizDisabled(this.baseLayoutChildren);
    this.cdref.markForCheck();
  }
  public getWizDisabled(): boolean {
    return this._wizDisabled;
  }

  @Input()
  undisableable: boolean = false;
}
